import { useEffect, useRef, useState } from 'react'
import Icon from './Icon'
import MetricEvidencePopover from './MetricEvidencePopover'
import Sparkline from './Sparkline'
import BarChart from './BarChart'
import styles from './MetricCard.module.css'

// Shared no-data label for empty bar/graph cards — one string, one .noData treatment,
// so the empty state is identical across card types.
const NO_DATA_LABEL = 'No data for this period'

/**
 * MetricCard — one KPI tile: an icon + name, a value, and a short description.
 *
 * When the metric carries `evidence`, the value gains a hover popover: hovering
 * reveals it, moving into it keeps it open (a short close-delay bridges the gap),
 * and clicking the value pins it open and focuses it so it can be scrolled/read.
 * Click-outside or Esc closes a pinned popover.
 *
 * Props (mirror the metric payload):
 *   name           {string} — metric label, shown next to the icon
 *   value          {number|string|object} — the value; shape depends on cardtype
 *   description    {string} — short line beneath the value
 *   cardtype       {'number'|'bar'|'graph'} — how to render the value. 'number'
 *                            prints it as-is; 'bar' treats value as a label→number
 *                            map (JSON-encoded string or object) and draws bars;
 *                            'graph' treats value as a JSON-encoded
 *                            {headline, unit, values:[], comparison:{pct,label}}
 *                            and draws a sparkline with a headline + delta.
 *   icon           {string} — icon name resolved by the shared <Icon> set
 *   caption        {string} — optional; small muted label pinned to the top-right
 *                            of the title row (e.g. "by effort" on the bar card).
 *   unit           {string} — optional; suffix appended to each bar value.
 *                            Defaults to '%' so existing percent bars are unchanged;
 *                            pass '' for raw counts.
 *   evidenceHeader {string} — optional; popover heading
 *   evidence       {string} — optional; if set, the value reveals an evidence popover
 *   compact        {boolean} — optional; renders a smaller, narrower tile (used in the
 *                            Skill Details modal header). Defaults to the full-size card.
 *   barColumns     {number} — optional; cardtype 'bar' only. Lays the bars out in this
 *                            many columns instead of one stacked column, so e.g. 4 bars
 *                            take 2 rows instead of 4. Defaults to 1 (stacked).
 *   maxBars        {number} — optional; cardtype 'bar' only. Caps the number of bars
 *                            drawn to the first N entries (the value map is already
 *                            sorted desc by the backend, so this keeps the top N).
 *                            Defaults to unlimited.
 *   tightBars      {boolean} — optional; cardtype 'bar' + barColumns > 1 only. See
 *                            BarChart's `tight` prop. Defaults to false, i.e. the
 *                            plain multi-column layout other bar cards (e.g. Team
 *                            page's Skill Levels tile) already use.
 *   narrow         {boolean} — optional; trims the title/value/description type scale
 *                            down a step. For rows that shrink this card's own box to
 *                            free up width for a sibling (e.g. Team page's bar chart
 *                            tile) — the type shrink is this component's job, the box
 *                            width is the row layout's.
 *   valueColor     {string} — optional; CSS color override for the number (cardtype: 'number' only)
 *   valueSize      {string} — optional; CSS font-size override for the number (cardtype: 'number' only)
 *   minHeight      {string} — optional; CSS min-height override
 */
export default function MetricCard({
  name, value, description = '', cardtype, icon, caption = '', unit = '%', barScale = '',
  errorRef = '', evidenceHeader = '', evidence = '', compact = false, barColumns = 1, narrow = false,
  valueColor = '', valueSize = '', minHeight = '', maxBars, tightBars = false,
}) {
  const [visible, setVisible] = useState(false)
  const [pinned,  setPinned]  = useState(false)
  const hideTimer  = useRef(null)
  const anchorRef  = useRef(null)
  const popoverRef = useRef(null)

  const hasEvidence = Boolean(evidence)
  const isBar = cardtype === 'bar'
  const isGraph = cardtype === 'graph'
  const isError = cardtype === 'error'
  // An empty bar renders its no-data state in the shared full-width region (like the
  // graph card), bypassing the indented value region so the "No data" line aligns
  // consistently across card types.
  const isBarEmpty = isBar && parseBars(value).length === 0

  // Hover intent: a brief close-delay lets the pointer cross the gap from the
  // value into the panel (and back) without the popover flickering shut.
  const show = () => { clearTimeout(hideTimer.current); setVisible(true) }
  const scheduleHide = () => {
    if (pinned) return
    clearTimeout(hideTimer.current)
    hideTimer.current = setTimeout(() => setVisible(false), 120)
  }
  const close = () => { clearTimeout(hideTimer.current); setPinned(false); setVisible(false) }
  const togglePin = () => { setVisible(true); setPinned(p => !p) }
  // Clicking the panel itself pins it, so the pointer can leave (even to another
  // screen) without dismissing it.
  const pin = () => { clearTimeout(hideTimer.current); setVisible(true); setPinned(true) }

  // Pin moves focus into the panel so it can be scrolled / read with the keyboard.
  useEffect(() => {
    if (pinned && visible) popoverRef.current?.focus()
  }, [pinned, visible])

  // A pinned popover is dismissed by clicking outside it or pressing Esc.
  useEffect(() => {
    if (!pinned) return
    const onDown = e => { if (anchorRef.current && !anchorRef.current.contains(e.target)) close() }
    const onKey  = e => { if (e.key === 'Escape') close() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [pinned])

  useEffect(() => () => clearTimeout(hideTimer.current), [])

  const valueKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePin() }
    else if (e.key === 'Escape') close()
  }

  // A metric that failed to generate: a self-contained card (no value/popover
  // machinery) showing which metric failed and a support-quotable reference. Styled
  // yellow — a partial failure, not a page-level break.
  if (isError) {
    return (
      <div className={`${styles.card} ${styles.errorCard} ${compact ? styles.compact : ''} ${narrow ? styles.narrow : ''}`}>
        <div className={styles.titleRow}>
          <span className={`${styles.icon} ${styles.errorIcon}`}><Icon name={icon || 'alert'} /></span>
          <span className={styles.title}>{name}</span>
        </div>
        <div className={styles.errorBody}>
          <div className={styles.errorMessage}>{description || 'This metric couldn’t be loaded.'}</div>
          {errorRef && (
            <div className={styles.errorRef} title="Quote this reference when contacting support.">
              Ref: <span className={styles.errorRefCode}>{errorRef}</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={`${styles.card} ${compact ? styles.compact : ''} ${narrow ? styles.narrow : ''} ${isBar ? styles.barCard : ''}`}
      style={minHeight ? { minHeight } : undefined}
    >

      {/* Icon + name (+ optional right-aligned caption) */}
      <div className={styles.titleRow}>
        <span className={styles.icon}><Icon name={icon} /></span>
        <span className={styles.title}>{name}</span>
        {caption && <span className={styles.caption}>{caption}</span>}
      </div>

      {/* Value — sparkline (graph), bar chart, or number. The graph spans the full
          card width; number/bar are centered and the inner wrap anchors the popover. */}
      {isGraph ? (
        <div className={styles.graphRegion}>
          <GraphValue value={value} />
        </div>
      ) : isBarEmpty ? (
        <div className={styles.graphRegion}>
          <div className={styles.noData}>{NO_DATA_LABEL}</div>
        </div>
      ) : (
        <div className={`${styles.valueRegion} ${isBar && tightBars ? styles.tightIndent : ''}`}>
          <div
            ref={anchorRef}
            className={`${styles.valueWrap} ${isBar ? styles.barWrap : ''}`}
            onMouseEnter={hasEvidence ? show : undefined}
            onMouseLeave={hasEvidence ? scheduleHide : undefined}
          >
            <div
              className={`${styles.valueInner} ${hasEvidence ? styles.clickable : ''}`}
              {...(hasEvidence && {
                role: 'button',
                tabIndex: 0,
                'aria-expanded': visible,
                onClick: togglePin,
                onKeyDown: valueKeyDown,
                onFocus: show,
                onBlur: scheduleHide,
              })}
            >
              {isBar
                ? <BarChart items={parseBars(value).slice(0, maxBars)} unit={unit} barScale={barScale} compact={compact} columns={barColumns} tight={tightBars} />
                : <span
                    className={styles.value}
                    style={{
                      ...(valueColor && { color: valueColor }),
                      ...(valueSize && { fontSize: valueSize }),
                    }}
                  >
                    {value}
                  </span>}
            </div>

            {hasEvidence && visible && (
              <MetricEvidencePopover
                ref={popoverRef}
                header={evidenceHeader}
                evidence={evidence}
                onMouseEnter={show}
                onMouseLeave={scheduleHide}
                onClick={pin}
              />
            )}
          </div>
        </div>
      )}

      {/* Description — omitted for bar tiles; the chart is self-describing, so the
          row doesn't need a caption line under it. */}
      {description && !isBar && (
        <div className={styles.description}>{description}</div>
      )}

    </div>
  )
}

// A bar value arrives as a JSON-encoded object (or already an object); flatten it
// to [{ label, value }] with numeric values, or [] if unparseable.
function parseBars(value) {
  let obj = value
  if (typeof value === 'string') {
    try { obj = JSON.parse(value) } catch { return [] }
  }
  if (!obj || typeof obj !== 'object') return []
  return Object.entries(obj).map(([label, v]) => ({ label, value: Number(v) || 0 }))
}

// A graph value arrives as a JSON-encoded object (or already an object):
// { headline, unit, values: [..], comparison: { pct, label } }. Returns null if
// unparseable so the card degrades to empty rather than throwing.
function parseGraph(value) {
  let obj = value
  if (typeof value === 'string') {
    try { obj = JSON.parse(value) } catch { return null }
  }
  if (!obj || typeof obj !== 'object') return null
  return obj
}

// The graph card body: a headline + unit, the sparkline, and a delta footer. The
// delta arrow follows the sign of `comparison.pct` and is rendered in one neutral
// colour (no good/bad semantics), matching the mockup.
function GraphValue({ value }) {
  const g = parseGraph(value)
  if (!g) return null
  const nums = (Array.isArray(g.values) ? g.values : []).map(Number).filter(Number.isFinite)
  const hasSeries = nums.length > 0
  // No headline and no plottable series → an explicit no-data state, matching the bar
  // card, so a correct-but-empty window (e.g. no resolved tickets in range) reads as
  // intentional rather than a broken card.
  if (g.headline == null && !hasSeries) {
    return <div className={styles.noData}>{NO_DATA_LABEL}</div>
  }
  const cmp = g.comparison
  const pct = cmp ? Number(cmp.pct) : null
  // The delta is a period-over-period comparison — meaningless with no series, so
  // suppress it when the series is empty rather than showing a figure with nothing behind it.
  const hasDelta = hasSeries && pct != null && Number.isFinite(pct)
  return (
    <div className={styles.graph}>
      <div className={styles.graphTop}>
        {g.headline != null && <span className={styles.graphHeadline}>{g.headline}</span>}
        {g.unit && <span className={styles.graphUnit}>{g.unit}</span>}
      </div>
      <Sparkline values={g.values} />
      {hasDelta && (
        <div className={styles.graphDelta}>
          <span className={styles.deltaArrow} aria-hidden="true">{pct < 0 ? '▼' : '▲'}</span>
          <span>{Math.abs(pct)}%{cmp.label ? ` ${cmp.label}` : ''}</span>
        </div>
      )}
    </div>
  )
}
